import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { toast } from 'sonner';
import { 
  Globe, 
  GlobeX, 
  UserSound, 
  CircleNotch,
  CheckCircle,
  XCircle,
  Shield
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const MultilingualSettings = ({ onUpdateSettings }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const [settings, setSettings] = useState({ multilingual: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError) throw new Error(accountIdError);

      const { data, error } = await supabase
        .from('vapi_call_settings')
        .select('multilingual')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          setSettings({ multilingual: false });
        } else {
          throw error;
        }
      } else {
        setSettings({
          multilingual: data?.multilingual || false
        });
      }
    } catch (error) {
      console.error('Error fetching multilingual settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      setIsSaving(true);
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();

      if (!accountId) throw new Error('User not authenticated');

      // Check if record exists
      const { data: existingData } = await supabase
        .from('vapi_call_settings')
        .select('id')
        .eq('account_id', accountId)
        .maybeSingle();

      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('vapi_call_settings')
          .update({ multilingual: newSettings.multilingual })
          .eq('account_id', accountId);
        if (error) throw error;
      } else {
        // Create new record with all required fields
        const { error } = await supabase
          .from('vapi_call_settings')
          .insert({
            account_id: accountId,
            voice: 'bella',
            multilingual: newSettings.multilingual,
            phone_number: '',
            timezone: 'America/New_York'
          });
        if (error) throw error;
      }

      setSettings(newSettings);

      // Call the callback to update parent components (like BrainSidebar)
      if (onUpdateSettings) {
        onUpdateSettings(newSettings);
      }

      toast.success(`Multilingual support ${newSettings.multilingual ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error saving multilingual settings:', error);
      toast.error('Failed to update multilingual settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (checked) => {
    saveSettings({ multilingual: checked });
  };

  const supportedLanguages = [
    'English',
    'Spanish', 
    'French',
    'French Creole',
    'Brazilian Portuguese',
    'Mandarin'
  ];

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto overscroll-none">
        <div className="pt-4 pb-8 px-8">
          {/* Header Skeleton */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20 rounded-3xl blur-3xl"></div>

            <div className="relative mb-5 py-8">
              <div className="flex items-center mb-6">
                {/* Icon Skeleton */}
                <div className="relative mr-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl animate-pulse"></div>
                </div>

                {/* Content Skeleton */}
                <div className="flex-1">
                  <div className="h-10 bg-gray-200 rounded-lg animate-pulse mb-3 w-80"></div>
                  <div className="h-6 bg-gray-100 rounded-lg animate-pulse w-96"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Card Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="space-y-6">
              {/* Toggle Section Skeleton */}
              <div>
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-40"></div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-300 rounded animate-pulse mr-3"></div>
                    <div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-48"></div>
                      <div className="h-4 bg-gray-100 rounded animate-pulse w-64"></div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-11 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Supported Languages Skeleton */}
              <div>
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-3 w-36"></div>
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="flex items-center p-3 rounded-lg border bg-gray-50 border-gray-200">
                      <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse mr-3"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How It Works Skeleton */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="w-5 h-5 bg-gray-300 rounded animate-pulse mt-0.5 mr-3 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-24"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-full"></div>
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-4/5"></div>
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto overscroll-none">
      <div className="pt-4 pb-8 px-8">
        {/* BEAUTIFUL STEVE JOBS HEADER - EXACTLY LIKE INTEGRATION DETAILS */}
        <div className="relative">
          {/* Subtle background glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20 rounded-3xl blur-3xl"></div>

          <div className="relative mb-5 py-8">
            <div className="flex items-center mb-6">
              {/* Premium Icon Container */}
              <div className="relative mr-6">
                <div className="w-20 h-20 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-gray-200/50 flex items-center justify-center backdrop-blur-sm">
                  <Globe className="w-10 h-10 text-gray-700" />
                </div>
                {/* Subtle inner shadow for depth */}
                <div className="absolute inset-0 rounded-3xl shadow-inner opacity-20"></div>
              </div>

              {/* Content */}
              <div className="flex-1">
                {/* Title with perfect typography */}
                <h1 className="text-4xl font-bold text-gray-900 tracking-tight mb-3">
                  Multilingual Support
                </h1>

                {/* Perfect Description */}
                <p className="text-lg text-gray-700 leading-relaxed font-light tracking-wide mb-4">
                  Enable your AI to communicate in multiple languages
                </p>


              </div>
            </div>


          </div>
        </div>

        {/* Main Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >


        {/* Toggle Section */}
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Language Support</h2>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Globe className="w-5 h-5 text-gray-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    Enable Multilingual Capabilities
                  </h3>
                  <p className="text-sm text-gray-600">
                    Allow your AI to communicate in multiple languages
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {isSaving && (
                  <CircleNotch className="w-4 h-4 animate-spin text-gray-400" />
                )}
                <Switch
                  checked={settings.multilingual}
                  onCheckedChange={handleToggle}
                  disabled={isSaving}
                  className="data-[state=checked]:bg-gray-900"
                />
              </div>
            </div>
          </div>

          {/* Supported Languages */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Supported Languages</h3>
            <div className="grid grid-cols-2 gap-3">
              {supportedLanguages.map((language, index) => (
                <div 
                  key={index}
                  className={cn(
                    "flex items-center p-3 rounded-lg border",
                    settings.multilingual
                      ? "bg-blue-50 border-blue-200"
                      : "bg-gray-50 border-gray-200 opacity-60"
                  )}
                >
                  <div className={cn(
                    "w-2 h-2 rounded-full mr-3",
                    settings.multilingual ? "bg-blue-500" : "bg-gray-400"
                  )}></div>
                  <span className={cn(
                    "text-sm font-medium",
                    settings.multilingual ? "text-blue-900" : "text-gray-600"
                  )}>
                    {language}
                  </span>
                </div>
              ))}
            </div>
          </div>



          {/* How It Works */}
          {settings.multilingual && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <UserSound className="w-5 h-5 text-gray-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">How It Works</h3>
                  <p className="text-sm text-gray-600">
                    Your AI automatically detects the language being spoken and responds in the same language. 
                    All supported languages are processed with the same level of accuracy.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
      </div>
    </div>
  );
};

export default MultilingualSettings; 